import {
  type CSSProperties,
  type HTMLAttributes,
  type MouseEventHandler,
  type PointerEventHandler,
  type PropsWithChildren,
  type RefObject,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { cn } from '../../utils'

type Direction = 'top' | 'bottom' | 'vertical' | 'left' | 'right' | 'horizontal'
type FadeSize = `${number}px` | `${number}%`

const DEFAULT_FADE_SIZE: FadeSize = '24px'
const DEFAULT_MIN_FADE_SIZE: FadeSize = '1px'

interface FadingScrollableStyle extends CSSProperties {
  '--fading-scroll-spread-start': string
  '--fading-scroll-spread-end': string
}

/**
 * Chrome floating over the scroller's bottom edge. The bottom `inset` px stay
 * transparent beneath it and content dissolves over `fadeSize` px above that.
 * A positive inset keeps the dissolve engaged with the scroller at its end,
 * since whatever sits under the chrome is unreadable anyway.
 */
export interface FadingScrollableBottomChrome {
  inset: number
  fadeSize: number
}

type Props = PropsWithChildren<
  Omit<HTMLAttributes<HTMLDivElement>, 'style'> & {
    /** Distance over which content fades at an active edge. */
    fadeSize?: FadeSize
    /** Resting spread at an inactive edge. Set to `0px` for no resting mask. */
    minFadeSize?: FadeSize
    direction?: Direction
    showScrollbar?: boolean
    showScrollbarOnHover?: boolean
    dragScroll?: boolean
    /** Pixels the pointer must move before we engage dragging (preserves simple clicks) */
    dragThreshold?: number
    /** Disable top fade effect */
    disableFadeTop?: boolean
    /** Disable bottom fade effect */
    disableFadeBottom?: boolean
    bottomChrome?: FadingScrollableBottomChrome
    /** Additional styles to merge with the default styles */
    style?: CSSProperties
  }
>

// Watching only the scroller misses content that lands after mount without
// changing its box (virtualized rows, hydrated messages, images): scrollHeight
// grows, no scroll event fires, and the fade stays off until the user scrolls.
function useResizeObserver<T extends HTMLElement>(targetRef: RefObject<T>, onResize: () => void) {
  useLayoutEffect(() => {
    const el = targetRef.current
    if (!el) return
    const ro = new ResizeObserver(() => onResize())
    ro.observe(el)
    for (const child of Array.from(el.children)) {
      ro.observe(child)
    }
    const mo = new MutationObserver((records) => {
      for (const record of records) {
        record.removedNodes.forEach((node) => {
          if (node instanceof Element) ro.unobserve(node)
        })
        record.addedNodes.forEach((node) => {
          if (node instanceof Element) ro.observe(node)
        })
      }
      onResize()
    })
    mo.observe(el, { childList: true })
    return () => {
      mo.disconnect()
      ro.disconnect()
    }
  }, [targetRef, onResize])
}

export const FadingScrollable = forwardRef<HTMLDivElement | undefined, Props>(
  (
    {
      children,
      className,
      direction,
      fadeSize = DEFAULT_FADE_SIZE,
      minFadeSize = DEFAULT_MIN_FADE_SIZE,
      showScrollbar,
      showScrollbarOnHover,
      dragScroll,
      dragThreshold = 6,
      disableFadeTop,
      disableFadeBottom,
      bottomChrome,
      style: externalStyle,
      ...props
    },
    ref,
  ) => {
    const scrollRef = useRef<HTMLDivElement>(null)
    const [scrollable, setScrollable] = useState<false | Direction>(false)
    const [isGrabbing, setIsGrabbing] = useState(false)
    const [canGrab, setCanGrab] = useState(false)

    const prevScrollableRef = useRef<false | Direction>(false)
    const prevCanGrabRef = useRef(false)

    // Expose the div via ref
    useImperativeHandle(ref, () => scrollRef.current ?? undefined, [])

    const computeScrollable = useCallback(() => {
      const el = scrollRef.current
      if (!el) return

      const wantsHorizontal = direction === 'horizontal' || direction === 'left' || direction === 'right'
      const wantsVertical =
        direction === undefined || direction === 'vertical' || direction === 'top' || direction === 'bottom'

      // Rapid flips while a streaming chat follow-scrolls (HIG-5961) are
      // absorbed by the 200ms transition on the mask's custom properties
      // (styles/index.css), so the fades derive directly from the edges.
      const canScrollUp = wantsVertical && el.scrollTop > 0
      const canScrollDown = wantsVertical && el.scrollHeight - el.clientHeight - el.scrollTop > 4
      const canScrollLeft = wantsHorizontal && el.scrollLeft > 0
      const canScrollRight = wantsHorizontal && el.scrollWidth - el.clientWidth - el.scrollLeft > 4

      let next: false | Direction = false

      if (wantsHorizontal && (canScrollLeft || canScrollRight)) {
        if (canScrollLeft && canScrollRight)
          next = direction === 'left' || direction === 'right' ? direction : 'horizontal'
        else if (canScrollLeft && !canScrollRight) next = direction === 'right' ? false : 'left'
        else if (!canScrollLeft && canScrollRight) next = direction === 'left' ? false : 'right'
      } else if (wantsVertical && (canScrollUp || canScrollDown)) {
        if (canScrollUp && canScrollDown) next = direction === 'top' || direction === 'bottom' ? direction : 'vertical'
        else if (canScrollUp && !canScrollDown) next = direction === 'bottom' ? false : 'top'
        else if (!canScrollUp && canScrollDown) next = direction === 'top' ? false : 'bottom'
      }

      const nextCanGrab = Boolean(dragScroll) && Boolean(next)

      if (prevScrollableRef.current !== next) {
        prevScrollableRef.current = next
        setScrollable(next)
      }
      if (prevCanGrabRef.current !== nextCanGrab) {
        prevCanGrabRef.current = nextCanGrab
        setCanGrab(nextCanGrab)
      }
    }, [direction, dragScroll])

    // Recompute on mount, scroll, and resize
    useEffect(() => {
      computeScrollable()
      const el = scrollRef.current
      if (!el) return

      let rafId: number | null = null
      const onScroll = () => {
        if (rafId != null) return // throttle to rAF
        rafId = requestAnimationFrame(() => {
          rafId = null
          computeScrollable()
        })
      }
      el.addEventListener('scroll', onScroll, { passive: true })
      return () => {
        if (rafId != null) cancelAnimationFrame(rafId)
        el.removeEventListener('scroll', onScroll)
      }
    }, [computeScrollable])

    useResizeObserver(scrollRef, computeScrollable)

    // Drag-to-scroll using Pointer Events + rAF inertia
    const dragState = useRef({
      armed: false, // pointerdown happened & eligible to become a drag
      dragging: false, // surpassed threshold
      pointerId: -1 as number,
      startX: 0,
      startY: 0,
      startLeft: 0,
      startTop: 0,
      vx: 0,
      vy: 0,
      lastT: 0,
      lastX: 0,
      lastY: 0,
      frame: 0 as number | 0,
    })

    const suppressClickRef = useRef(false)

    const friction = 0.95 // slows down each frame
    const stopSpeed = 0.5 // cutoff velocity to stop

    const animate = useCallback(() => {
      const el = scrollRef.current
      if (!el) return
      const s = dragState.current
      el.scrollLeft -= s.vx
      el.scrollTop -= s.vy
      s.vx *= friction
      s.vy *= friction
      if (Math.abs(s.vx) > stopSpeed || Math.abs(s.vy) > stopSpeed) {
        s.frame = requestAnimationFrame(animate)
      } else {
        s.dragging = false
      }
    }, [])

    const onPointerDown = useCallback<PointerEventHandler<HTMLDivElement>>(
      (e) => {
        if (!dragScroll || !scrollable) return
        if (e.button !== 0) return // primary only
        const el = scrollRef.current
        if (!el) return

        const s = dragState.current
        s.armed = true
        s.dragging = false
        s.pointerId = e.pointerId
        s.startX = e.clientX
        s.startY = e.clientY
        s.startLeft = el.scrollLeft
        s.startTop = el.scrollTop
        s.lastT = performance.now()
        s.lastX = e.clientX
        s.lastY = e.clientY
        if (s.frame) cancelAnimationFrame(s.frame)

        // capture so we keep getting move/up even if pointer leaves the element
      },
      [dragScroll, scrollable],
    )

    const onPointerMove = useCallback<PointerEventHandler<HTMLDivElement>>(
      (e) => {
        const el = scrollRef.current
        if (!el) return
        const s = dragState.current
        if (!s.armed) return

        const x = e.clientX
        const y = e.clientY
        const dx = x - s.startX
        const dy = y - s.startY

        if (!s.dragging) {
          if (Math.hypot(dx, dy) < dragThreshold) return
          s.dragging = true
          setIsGrabbing(true)
          // prevent text selection only after we decided to drag
          ;(document.body as HTMLBodyElement).style.userSelect = 'none'
          // capture now that we're officially dragging so child clicks still work when we don't drag
          ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
          // ensure the click generated by this drag is suppressed
          suppressClickRef.current = true
        }

        e.preventDefault()
        el.scrollLeft = s.startLeft - dx
        el.scrollTop = s.startTop - dy

        // velocity calc
        const now = performance.now()
        const dt = now - s.lastT || 1
        s.vx = ((x - s.lastX) / dt) * 16
        s.vy = ((y - s.lastY) / dt) * 16
        s.lastT = now
        s.lastX = x
        s.lastY = y
      },
      [dragThreshold],
    )

    const endDrag = useCallback(() => {
      const el = scrollRef.current
      const s = dragState.current
      if (!el || !s.armed) return

      if (s.pointerId !== -1 && el.hasPointerCapture(s.pointerId)) {
        el.releasePointerCapture(s.pointerId)
      }

      if (!s.dragging) {
        // simple click
        s.armed = false
        s.pointerId = -1
        return
      }

      s.armed = false
      setIsGrabbing(false)
      ;(document.body as HTMLBodyElement).style.userSelect = ''
      s.frame = requestAnimationFrame(animate)
      s.pointerId = -1
    }, [animate])

    const onPointerUp = useCallback<PointerEventHandler<HTMLDivElement>>(() => endDrag(), [endDrag])
    const onPointerCancel = useCallback<PointerEventHandler<HTMLDivElement>>(() => endDrag(), [endDrag])

    const onClickCapture = useCallback<MouseEventHandler<HTMLDivElement>>((e) => {
      if (suppressClickRef.current) {
        suppressClickRef.current = false // consume one synthetic click after a drag
        e.preventDefault()
        e.stopPropagation()
      }
    }, [])

    // Cleanup any outstanding rAF / body styles on unmount
    useEffect(() => {
      return () => {
        const s = dragState.current
        if (s.frame) cancelAnimationFrame(s.frame)
        ;(document.body as HTMLBodyElement).style.userSelect = ''
      }
    }, [])

    const wantsHorizontalMask = direction === 'horizontal' || direction === 'left' || direction === 'right'
    const allowsStartFade = wantsHorizontalMask ? direction !== 'right' : direction !== 'bottom' && !disableFadeTop
    const allowsEndFade = wantsHorizontalMask ? direction !== 'left' : direction !== 'top' && !disableFadeBottom
    const startFadeActive =
      scrollable === 'top' || scrollable === 'vertical' || scrollable === 'left' || scrollable === 'horizontal'
    const spreadStart = !allowsStartFade ? '0px' : startFadeActive ? fadeSize : minFadeSize
    const bottomChromeInset = bottomChrome?.inset
    const expandedEndSpread = bottomChrome ? `${bottomChrome.fadeSize}px` : fadeSize
    const keepEndFade = !wantsHorizontalMask && !!bottomChromeInset && allowsEndFade
    const endFadeActive =
      scrollable === 'bottom' || scrollable === 'vertical' || scrollable === 'right' || scrollable === 'horizontal'
    const spreadEnd = !allowsEndFade ? '0px' : keepEndFade || endFadeActive ? expandedEndSpread : minFadeSize

    const style = useMemo<CSSProperties>(() => {
      const startStop = 'var(--fading-scroll-spread-start)'
      const endStop = 'var(--fading-scroll-spread-end)'
      let mask: string
      if (wantsHorizontalMask) {
        mask = `linear-gradient(to right, transparent 0%, black ${startStop}, black calc(100% - ${endStop}), transparent 100%)`
      } else if (bottomChromeInset !== undefined) {
        const bottomOpaque = `calc(100% - ${bottomChromeInset}px)`
        const bottomFade = `calc(${bottomOpaque} - var(--fading-scroll-spread-end))`
        mask = `linear-gradient(to bottom, transparent 0, black ${startStop}, black ${bottomFade}, transparent ${bottomOpaque}, transparent 100%)`
      } else {
        mask = `linear-gradient(to bottom, transparent 0%, black ${startStop}, black calc(100% - ${endStop}), transparent 100%)`
      }

      const internalStyle: FadingScrollableStyle = {
        WebkitMaskImage: mask,
        maskImage: mask,
        ['--fading-scroll-spread-start']: spreadStart,
        ['--fading-scroll-spread-end']: spreadEnd,
        touchAction: dragScroll ? 'none' : undefined,
      }

      return externalStyle ? { ...internalStyle, ...externalStyle } : internalStyle
    }, [wantsHorizontalMask, spreadStart, spreadEnd, dragScroll, bottomChromeInset, externalStyle])

    const cursorClass = useMemo(() => {
      if (!dragScroll || !canGrab) return undefined
      return isGrabbing ? 'cursor-grabbing' : 'cursor-grab'
    }, [dragScroll, canGrab, isGrabbing])

    const wantsHorizontal = direction === 'horizontal' || direction === 'left' || direction === 'right'
    const wantsVertical =
      direction === undefined || direction === 'vertical' || direction === 'top' || direction === 'bottom'

    return (
      <div
        ref={scrollRef}
        className={cn(
          'fading-scrollable',
          wantsHorizontal && !wantsVertical && 'overflow-x-auto overflow-y-hidden',
          wantsVertical && !wantsHorizontal && 'overflow-y-auto overflow-x-hidden',
          wantsHorizontal && wantsVertical && 'overflow-auto',
          showScrollbarOnHover && 'hover-visible-scrollbar',
          showScrollbar !== true && !showScrollbarOnHover && 'invisible-scrollbar',
          cursorClass,
          className,
        )}
        style={style}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
        onClickCapture={onClickCapture}
        {...props}
      >
        {children}
      </div>
    )
  },
)

FadingScrollable.displayName = 'FadingScrollable'
